import { Menu as MenuIcon } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import { useAnalytics } from '@praxis/analytics/client'
import {
  AppBar,
  Button,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import appConfig from '../../appConfig'
import applyProps from '../../appConfig/applyConfig'
import useScrollSpy from '../../hooks/useScrollSpy'
import TargetPlus from '../common/Icons/TargetPlus'
import styles from './header.module.scss'
import {
  CLICK_HEADER_TAB_HOME,
  CLICK_HEADER_TAB_WHY_TARGET_PLUS,
  CLICK_HEADER_TAB_HOW_IT_WORKS,
  CLICK_HEADER_TAB_RESOURCES,
  CLICK_HEADER_TAB_GET_STARTED,
  CLICK_HEADER_TAB_GROW,
  CLICK_HEADER_TAB_SERVICE_PROVIDERS,
  CLICK_HEADER_TAB_SUCCESS_STORIES,
  CLICK_HEADER_TAB_EVENTS,
  CLICK_HEADER_SIGN_IN_BUTTON,
  CLICK_HEADER_APPLY_NOW_BUTTON,
  CLICK_HEADER_MOBILE_SIGN_IN,
  CLICK_HEADER_MOBILE_MENU_TOGGLE,
  CLICK_HEADER_LOGO_HOME,
} from '../../analytics/events'
import Icon, {
  ChevronRightIcon,
  ChevronDownIcon,
} from '@design-systems/react-icons'
import { Popover } from '@design-systems/canvas-ui'
import { PopoverContent } from './popoverContent'
import { flag } from '../../flag/index'
import { MenuDrawer } from './MenuDrawer'

const MenuHeaderDefault = [
  'home',
  'why-target-plus',
  'how-it-works',
  'resources',
]
const MenuHeaderRevamp = [
  'home',
  'get-started',
  'grow',
  'service-providers',
  'success-stories',
  'events',
]

const Header = () => {
  const isTplusRevampFlagEnabled = flag('TPLUS_REVAMP')

  const MenuHeader = isTplusRevampFlagEnabled
    ? MenuHeaderRevamp
    : MenuHeaderDefault
  const { sendClickEvent } = useAnalytics()
  const location = useLocation()
  const isHomePage = location.pathname === '/'
  const isApplyPage = ['/apply', '/apply/v2', '/apply/v2/success'].includes(
    location.pathname,
  )
  const [selectedMenu, setSelectedMenu] = useState(
    isHomePage ? MenuHeader[0] : null,
  )
  const [openPopoverId, setOpenPopoverId] = useState(null)
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false)

  useScrollSpy(isHomePage ? MenuHeader : [], setSelectedMenu)

  useEffect(() => {
    if (!isHomePage) {
      const pathnameId = location.pathname.replace('/', '')
      setSelectedMenu(pathnameId || null)
      return
    }

    if (!location.hash) {
      setSelectedMenu(MenuHeader[0])
    }
  }, [isHomePage, location.pathname, location.hash, MenuHeader])

  // State management with hooks
  const [menuAnchors, setMenuAnchors] = useState({
    desktop: null,
    mobile: null,
  })
  const isMobileMenuOpen = Boolean(menuAnchors.mobile)

  const handleButtonClick = useCallback(
    (title) => {
      sendClickEvent(title)
    },
    [sendClickEvent],
  )

  const handleMobileMenuClose = useCallback(() => {
    setMenuAnchors((prev) => ({ ...prev, mobile: null }))
  }, [])

  const handleMobileMenuOpen = useCallback(
    (event) => {
      handleButtonClick(CLICK_HEADER_MOBILE_MENU_TOGGLE)
      setMenuAnchors((prev) => ({
        ...prev,
        mobile: isTplusRevampFlagEnabled ? null : event.currentTarget,
      }))
      setIsMobileDrawerOpen((prev) => !prev)
    },
    [handleButtonClick, isTplusRevampFlagEnabled],
  )

  const renderMenuItems = useCallback(
    (isDesktopView = false) => {
      const defaultOptions = [
        isDesktopView
          ? {
              id: 'home',
              title: 'Home',
              href: '/#home',
              type: 'text',
              eventId: CLICK_HEADER_TAB_HOME,
            }
          : {
              title: 'Sign in',
              href: appConfig.sellerUrls.portal,
              type: 'contained',
              eventId: CLICK_HEADER_MOBILE_SIGN_IN,
            },
        {
          id: 'why-target-plus',
          title: 'Why Target Plus',
          href: '/#why-target-plus',
          type: 'text',
          eventId: CLICK_HEADER_TAB_WHY_TARGET_PLUS,
        },
        {
          id: 'how-it-works',
          title: 'How it works',
          href: '/#how-it-works',
          type: 'text',
          eventId: CLICK_HEADER_TAB_HOW_IT_WORKS,
        },
        {
          id: 'resources',
          title: 'Resources',
          href: '/#resources',
          type: 'text',
          shouldRenderMenu: true,
          eventId: CLICK_HEADER_TAB_RESOURCES,
        },
      ]

      const revampOptions = [
        isDesktopView
          ? {
              id: 'home',
              title: 'Home',
              href: '/#home',
              type: 'text',
              eventId: CLICK_HEADER_TAB_HOME,
            }
          : {
              title: 'Sign in',
              href: appConfig.sellerUrls.portal,
              type: 'contained',
              eventId: CLICK_HEADER_MOBILE_SIGN_IN,
            },
        {
          id: 'get-started',
          title: 'Get started',
          type: 'text',
          hasMegaMenu: true,
          eventId: CLICK_HEADER_TAB_GET_STARTED,
        },
        {
          id: 'grow',
          title: 'Grow',
          href: '/#grow',
          type: 'text',
          hasMegaMenu: true,
          eventId: CLICK_HEADER_TAB_GROW,
        },
        {
          id: 'service-providers',
          title: 'Service providers',
          href: '/service-providers',
          type: 'text',
          eventId: CLICK_HEADER_TAB_SERVICE_PROVIDERS,
        },
        {
          id: 'success-stories',
          title: 'Success stories',
          href: '/success-stories',
          type: 'text',
          eventId: CLICK_HEADER_TAB_SUCCESS_STORIES,
        },
        {
          id: 'events',
          title: 'Events',
          href: '/events',
          type: 'text',
          eventId: CLICK_HEADER_TAB_EVENTS,
        },
      ]

      const options = isTplusRevampFlagEnabled ? revampOptions : defaultOptions

      const getButtonClassName = (option, isDesktopView) => {
        const classNames = [styles.menuItem]

        if (option.type === 'contained') {
          classNames.push(styles.signInButton)
        }

        if (!isDesktopView) {
          classNames.push(styles.textColor)
        } else {
          classNames.push(styles.textColorWhite)
          if (option.id === selectedMenu) {
            classNames.push(styles.selectedMenu)
          }
        }
        return classNames.join(' ')
      }

      return options.map((option) => {
        const buttonElement = (
          <Button
            disableRipple
            className={getButtonClassName(option, isDesktopView)}
            href={option?.href ?? ''}
            onClick={() => {
              handleButtonClick(option?.eventId)
              handleMobileMenuClose()
            }}
            variant={option?.type}
          >
            {option?.title}
          </Button>
        )

        // For mobile view, wrap in MenuItem for proper ARIA roles
        if (!isDesktopView) {
          return (
            <MenuItem disableRipple key={option?.href || option?.title}>
              {buttonElement}
            </MenuItem>
          )
        }

        if (option.hasMegaMenu) {
          return (
            <div className={styles.tab} key={option?.href || option?.title}>
              <Popover
                key={`${option.id}-${location.pathname}`}
                location="bottom-left"
                content={<PopoverContent selectedHeaderOption={option.id} />}
                onRequestOpen={() => setOpenPopoverId(option.id)}
                onRequestClose={() => setOpenPopoverId(null)}
                className={`${getButtonClassName(option, isDesktopView)} ${styles.popoverContent}`}
              >
                {option.title}
                <Icon
                  svg={
                    openPopoverId === option.id
                      ? ChevronDownIcon
                      : ChevronRightIcon
                  }
                  className="hc8-pl-2x hc8-pt-1x"
                  size="inline"
                />
              </Popover>
            </div>
          )
        }

        // For desktop view, return button directly without MenuItem wrapper
        return (
          <div className={styles.tab} key={option?.href || option?.title}>
            {buttonElement}
          </div>
        )
      })
    },
    [
      isTplusRevampFlagEnabled,
      selectedMenu,
      handleButtonClick,
      handleMobileMenuClose,
      openPopoverId,
      location.pathname,
    ], // Added handleButtonClick
  )

  const renderDesktopMenus = useCallback(() => {
    return (
      <div className={styles.headerDesktopSection}>{renderMenuItems(true)}</div>
    )
  }, [renderMenuItems])

  const renderMobileMenu = useCallback(() => {
    return isTplusRevampFlagEnabled ? (
      <MenuDrawer
        isOpen={isMobileDrawerOpen}
        onClose={() => setIsMobileDrawerOpen(false)}
      />
    ) : (
      <Menu
        anchorEl={menuAnchors.mobile}
        open={isMobileMenuOpen}
        onClose={handleMobileMenuClose}
      >
        {renderMenuItems()}
      </Menu>
    )
  }, [
    menuAnchors.mobile,
    isMobileMenuOpen,
    isMobileDrawerOpen,
    isTplusRevampFlagEnabled,
    handleMobileMenuClose,
    renderMenuItems,
  ])

  // Memoized components for better performance
  const desktopMenus = useMemo(() => renderDesktopMenus(), [renderDesktopMenus])
  const mobileMenu = useMemo(() => renderMobileMenu(), [renderMobileMenu])

  return (
    <AppBar
      className={styles.headerAppBar}
      data-testid="header"
      position="sticky"
    >
      <Toolbar className={styles.headerRoot} disableGutters>
        <Grid
          container
          className={styles.headerContainer}
          justifyContent="space-between"
          alignItems={'center'}
        >
          {/* Target Plus Logo */}
          <Grid item>
            <IconButton
              disableRipple
              className={styles.headerDisableHoverIconButton}
              href="/"
              size="large"
              aria-label="target plus home"
              onClick={() => handleButtonClick(CLICK_HEADER_LOGO_HOME)}
            >
              <TargetPlus fill="#FFF" />
            </IconButton>
          </Grid>

          {/* Options */}
          <Grid item className="desktop-menu-items-container">
            {desktopMenus}
          </Grid>

          {/* CTAs */}
          <Grid item className={styles.headerDesktopSection}>
            <Button
              className={styles.headerSignInButton}
              variant="contained"
              href={appConfig.sellerUrls.portal}
              onClick={() => handleButtonClick(CLICK_HEADER_SIGN_IN_BUTTON)}
            >
              Sign in
            </Button>
            {!isApplyPage && (
              <Button
                className={styles.headerApplyButton}
                variant="outlined"
                {...applyProps}
                aria-label="Apply now"
                onClick={() => {
                  setSelectedMenu(null)
                  handleButtonClick(CLICK_HEADER_APPLY_NOW_BUTTON)
                }}
              >
                Apply now
              </Button>
            )}
          </Grid>

          {/* hamburger menu */}
          <div className={styles.headerMobileSection}>
            <IconButton
              aria-label="menu"
              aria-haspopup="true"
              onClick={handleMobileMenuOpen}
              color="inherit"
              size="large"
            >
              {isMobileMenuOpen && !isTplusRevampFlagEnabled ? (
                <CloseIcon
                  sx={{ width: 32, height: 32 }}
                  onClick={handleMobileMenuClose}
                />
              ) : (
                <MenuIcon sx={{ width: 32, height: 32 }} />
              )}
            </IconButton>
          </div>
        </Grid>
        {mobileMenu}
      </Toolbar>
    </AppBar>
  )
}

export default Header
